import React, { useState, useEffect, useMemo } from 'react';
import MacTable from '../ui/MacTable';
import VisitLink from '../ui/VisitLink';
import '../../styles/findings.css';

// Import utilities
import { getData } from '../../utils/dataUtils';
import { formatDate } from '../../utils/dateUtils';
import { TABLE_HEADERS } from '../../utils/constants';

// Group audits by organization (pure function, hoisted outside component)
const groupAuditsByOrganization = (auditList) => {
  return auditList.reduce((groups, audit) => {
    const orgName = audit.organization || 'Other';
    if (!groups[orgName]) {
      groups[orgName] = {
        audits: [],
        url: audit.organizationURL || audit.organizationUrl || ''
      };
    }
    groups[orgName].audits.push(audit);
    return groups;
  }, {});
};

const AuditsSection = () => {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  useEffect(() => {
    // Helper function to initialize group expansion state
    const initializeGroups = (auditsList) => {
      const initialGroupState = {};
      const grouped = groupAuditsByOrganization(auditsList);
      Object.keys(grouped).forEach(orgName => {
        initialGroupState[orgName] = false;
      });
      setExpandedGroups(initialGroupState);
    };

    const loadAudits = async () => {
      try {
        setLoading(true);
        
        try {
          // Get data from local JSON file with sorting
          const data = getData('audits', { sort: 'date', sortOrder: 'desc' });
          setAudits(data);
          initializeGroups(data);
        } catch (err) {
          setError('Failed to load audit data.');
        }
        
        setLoading(false);
      } catch (err) {
        setError('An unexpected error occurred.');
        setLoading(false);
      }
    };
    
    loadAudits();
  }, []);

  const toggleGroupExpand = (orgName, e) => {
    e.preventDefault();
    setExpandedGroups(prev => ({
      ...prev,
      [orgName]: !prev[orgName]
    }));
  };

  const groupedAudits = useMemo(() => groupAuditsByOrganization(audits), [audits]);

  const renderRow = (item, index) => (
    <tr key={item.id || index}>
      <td>
        {item.projectURL ? 
          <a href={item.projectURL} target="_blank" rel="noopener noreferrer">{item.project}</a> : 
          item.isPublished ? item.project : <img src="images/clock-face-four-oclock_1f553.gif" alt="clock" loading="lazy" width="16" height="16" />
        }
      </td>
      <td>{item.type}</td>
      <td>{formatDate(item.date)}</td>
      <td>
        {item.reportURL ? (
          <VisitLink url={item.reportURL} placeholder="view" />
        ) : (
          <span className="unpublished">{item.isPublished === false ? 'Undisclosed' : 'N/A'}</span>
        )}
      </td>
    </tr>
  );

  if (loading) {
    return (
      <section id="audits">
        <div className="container">
          <h2 className="topic">Private Audit Engagements</h2>
          <div className="loading">Loading audits...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="audits">
        <div className="container">
          <h2 className="topic">Private Audit Engagements</h2>
          <div className="error">{error}</div>
        </div>
      </section>
    );
  }

  return (
    <section id="audits">
      <div className="container">
        <h2 className="topic">Private Audit Engagements</h2>

        {/* Render a separate table for each organization */}
        {Object.entries(groupedAudits).map(([orgName, orgData]) => {
          // Filter audits based on expanded state
          const displayAudits = expandedGroups[orgName]
            ? orgData.audits
            : orgData.audits.filter(audit => audit.isPublished).slice(0, 5);
            
          return (
            <div key={orgName} className="audit-group">
              <MacTable 
                title={<>{orgName} {orgData.url && <>| <VisitLink url={orgData.url} placeholder="visit" /></>}</>}
                headers={TABLE_HEADERS.AUDITS} 
                data={displayAudits} 
                renderRow={renderRow}
                showViewMore={orgData.audits.length > 5}
                onViewMoreClick={(e) => toggleGroupExpand(orgName, e)}
                viewMoreText={expandedGroups[orgName] ? 'Show Less' : 'View More'}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AuditsSection;
